import React, { useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty, times } from 'lodash-es';

import TableActionsRow from './TableActionsRow';
import EmptyReportsTable from '../ExportsTable/EmptyReportsTable';
import TablePerPageDropdown from '../../TablePerPageDropdown';
import Skeleton from '../../../../../components/Skeleton';
import Pagination from '../../../../../components/Pagination';
import SearchField from '../../../../../components/Search/SearchField';
import { SortableTable } from '../../../../../components/SortableTable';
import ErrorStateWithReload from '../../../../../components/ErrorStateWithReload';

import api from '../../../api';
import { columns } from '../../../configs/custom-reports-config';
import { numberToBytes } from '../../../../../utils/bytes';

const ReportsTable = ({
  tableData,
  filters,
  settings,
  onFiltersChange,
  onVersionsHistoryRequest,
  onPageChange,
  onPerPageChange,
  hasFirstVisitAlert,
}) => {
  const handleSearchChange = (query) => {
    onFiltersChange({ query, offset: 0 });
  };

  const handleSort = ({ sortBy, sortOrder }) => {
    onFiltersChange({
      order_by: sortBy,
      order_dir: sortOrder,
      offset: 0,
    });
  };

  const handlePageChange = ({ selected }) => {
    onPageChange({
      offset: tableData.length,
      limit: filters.limit,
      current_page: selected,
    });
  };

  const handleOnAlertClick = (report) => {
    api.customReports[report['subscribed?'] ? 'unsubscribe' : 'subscribe'](report.id)
      .then(() => onFiltersChange(filters));
  };

  const loadingRows = useMemo(() => (
    times(10, n => ({
      key: n,
      name: <Skeleton height={16} width="60%" />,
      frequency: <Skeleton height={16} width="80%" />,
      time_period: <Skeleton height={16} />,
      updated_at: <Skeleton height={16} width="70%" />,
      downloaded_at: <Skeleton height={16} width="70%" />,
      version_size: <Skeleton height={16} width="70%" />,
      actions: (
        <div className="report-actions-cell d-flex">
          <Skeleton height={24} width={24} />
          <Skeleton height={24} width={24} />
          <Skeleton height={24} width={24} />
        </div>
      ),
    }))
  ), []);

  const rows = tableData.map((report, i) => ({
    name: (
      <span className="d-flex">
        <span className="text-truncate">{report.name}</span>
        {report.status === 'New' &&
          <span className="badge text-capitalize ml-xs">New</span>}
        {report.status === 'Updated' &&
          <span className="badge text-capitalize ml-xs">Updated</span>}
      </span>
    ),
    frequency: report.frequency,
    time_period: report.time_period,
    updated_at: report.updated_at,
    downloaded_at: report.downloaded_at,
    version_size: numberToBytes(report.version_size),
    actions: <TableActionsRow
      report={report}
      showPromotionTooltip={i === 0 && hasFirstVisitAlert}
      onReportAlert={() => handleOnAlertClick(report)}
      onReportTimer={() => onVersionsHistoryRequest(report.id)}
    />,
  }));

  // Pagination
  const total = parseInt(filters.pagination.total, 10);
  const perPage = parseInt(filters.pagination.per_page, 10);
  const currentPage = parseInt(filters.pagination.current_page, 10);
  const showPagination = total > tableData.length;

  if (settings.error) return <ErrorStateWithReload />;

  return (
    <div className="custom-reports-table-box">
      <div className="custom-exports__table-header">
        <div className="custom-exports__table-header-actions">
          <SearchField
            size="sm"
            disabled={isEmpty(tableData)}
            placeholder="Search Reports"
            onChange={handleSearchChange}
          />
        </div>
        <div className="custom-exports__table-header-actions">
          <div className="custom-exports__table-header-actions-text">
            <p>Need to export complex data?  Our team can help.</p>
            <p>
              <a href="mailto:support@apptopia.com?subject=Request for Custom Reports data&body=Dear Customer Success, I am interested in getting customized data reports from Apptopia. I would like to discuss my requirements with you.">
                Email Customer Success
              </a>
            </p>
          </div>
          <TablePerPageDropdown
            active={perPage}
            total={total}
            disabled={total === 0}
            onChange={onPerPageChange}
          />
        </div>
      </div>
      <SortableTable
        tableClassName={cx('table-default custom-reports-table table-default-max-padding sticky-table-header', { 'no-exports': !tableData.length })}
        style={{ tableLayout: 'fixed' }}
        defaultSortBy="created_at_and_subscribed"
        columns={columns}
        rows={settings.pending ? loadingRows : rows}
        initialRowsLimit={perPage}
        showMoreCellColSpan={columns.length}
        emptyStateText={<EmptyReportsTable />}
        hasShowMoreCell={false}
        onSortEnd={handleSort}
      />
      {showPagination && (
        <Pagination
          total={total}
          perPage={perPage}
          currentPage={currentPage}
          className="mt-sm"
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

ReportsTable.propTypes = {
  tableData: T.array.isRequired,
  filters: T.object.isRequired,
  settings: T.object.isRequired,
  onFiltersChange: T.func.isRequired,
  onVersionsHistoryRequest: T.func.isRequired,
  onPageChange: T.func.isRequired,
  onPerPageChange: T.func.isRequired,
  hasFirstVisitAlert: T.bool.isRequired,
};

export default ReportsTable;
